/**
 * Portal Toggle Component
 *
 * A toggle switch that allows IT staff to switch between User and IT portals.
 * Only visible to users who have access to both portals.
 */

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortal, type PortalType } from '../../../contexts/PortalContext';
import './PortalToggle.css';

interface PortalOption {
  id: PortalType;
  label: string;
  icon: React.ReactNode;
  defaultPath: string;
}

const PORTAL_OPTIONS: PortalOption[] = [
  {
    id: 'user',
    label: 'User',
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="8" cy="5" r="3" />
        <path d="M2 14c0-2.5 2.5-4 6-4s6 1.5 6 4" strokeLinecap="round" />
      </svg>
    ),
    defaultPath: '/help',
  },
  {
    id: 'it',
    label: 'IT',
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="2" y="3" width="12" height="8" rx="1" />
        <path d="M5 14h6M8 11v3" strokeLinecap="round" />
      </svg>
    ),
    defaultPath: '/ticketing/my-work',
  },
];

export function PortalToggle() {
  const { activePortal, hasITAccess, setPortal } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();

  const handlePortalChange = useCallback(
    (portal: PortalType) => {
      if (portal === activePortal) return;

      setPortal(portal);

      // Navigate to the appropriate default page for the new portal
      const targetOption = PORTAL_OPTIONS.find((opt) => opt.id === portal);
      if (targetOption) {
        // Only navigate if we're not already in a valid path for the new portal
        const isUserPortalPath =
          location.pathname.startsWith('/help') ||
          location.pathname.startsWith('/kb') ||
          location.pathname === '/';

        const isITPortalPath =
          location.pathname.startsWith('/ticketing') ||
          location.pathname.startsWith('/tickets') ||
          location.pathname.startsWith('/admin') ||
          location.pathname.startsWith('/knowledge-base');

        // Knowledge base is shared, so don't navigate away from it
        const isSharedPath =
          location.pathname.startsWith('/kb') ||
          location.pathname.startsWith('/knowledge-base');

        if (isSharedPath) {
          // Stay on shared pages
          return;
        }

        if (portal === 'user' && !isUserPortalPath) {
          navigate(targetOption.defaultPath);
        } else if (portal === 'it' && !isITPortalPath) {
          navigate(targetOption.defaultPath);
        }
      }
    },
    [activePortal, setPortal, navigate, location.pathname]
  );

  // Don't render if user doesn't have IT access
  if (!hasITAccess) {
    return null;
  }

  return (
    <div className="portal-toggle" role="tablist" aria-label="Portal selection">
      {PORTAL_OPTIONS.map((option) => {
        const isActive = activePortal === option.id;

        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`portal-toggle__option ${
              isActive ? 'portal-toggle__option--active' : ''
            }`}
            onClick={() => handlePortalChange(option.id)}
          >
            <span className="portal-toggle__icon">{option.icon}</span>
            <span className="portal-toggle__label">{option.label}</span>
          </button>
        );
      })}
      <div
        className="portal-toggle__indicator"
        style={{
          transform: `translateX(${activePortal === 'user' ? '0' : '100%'})`,
        }}
      />
    </div>
  );
}
